import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { FaCalendarAlt, FaEye, FaPlus } from "react-icons/fa";
import styles from "../List.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import { fetchClassOptions } from "../../../../redux/Masterdata";
import {
  CustomDropdown,
  CustomTextInput,
  CustomButton,
} from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";

const ExamScheduleList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const school_id = clientData?.school_id || "";
  const academic_year = clientData?.academic_year || "";
  const academic_year_id = clientData?.academic_year_id || "";
  const { classOptions } = useSelector((state) => state.masterData);
  const { translations } = useSelector((state) => state.language);

  const [loading, setLoading] = useState(false);
  const [scheduleList, setScheduleList] = useState([]);
  const [selectedExam, setSelectedExam] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [examNameOptions, setExamNameOptions] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  // Fetch exam name options
  const fetchExamNames = useCallback(async () => {
    try {
      const payload = {
        school_id: parseInt(school_id),
        academic_year: parseInt(academic_year_id),
      };
      const response = await AxiosInstance.post(
        "/exam/examConfig/list",
        payload
      );
      if (response?.data?.status) {
        const examData = response.data.data || [];
        const options = [
          {
            value: "",
            label: translations.labels?.allExams || "All Exams",
          },
          ...examData.map((item) => ({
            value: item.id,
            label: item.exam_type || item.exam_name,
          })),
        ];
        setExamNameOptions(options);
      }
    } catch (error) {
      console.error("Error fetching exam names:", error);
    }
  }, [school_id, academic_year_id, translations]);

  // Fetch exam schedule list
  const fetchScheduleList = useCallback(async () => {
    try {
      setLoading(true);
      const payload = {
        school_id: parseInt(school_id),
        academic_id: parseInt(academic_year_id),
        config_id: selectedExam?.value || "",
        class_id: selectedClass?.value || "",
      };
      const response = await AxiosInstance.post(
        "/exam/examSchedule/list",
        payload
      );
      if (response?.data?.status) {
        setScheduleList(response.data.data || []);
      } else {
        setScheduleList([]);
      }
    } catch (error) {
      console.error("Error fetching schedule list:", error);
      setScheduleList([]);
    } finally {
      setLoading(false);
    }
  }, [school_id, academic_year_id, selectedExam, selectedClass]);

  useEffect(() => {
    fetchExamNames();
    dispatch(
      fetchClassOptions({
        id: parseInt(school_id),
        academic_year: academic_year,
      })
    );
  }, [dispatch, school_id, academic_year, fetchExamNames]);

  useEffect(() => {
    fetchScheduleList();
  }, [fetchScheduleList]);

  // Class dropdown options with "All" option
  const classDropdownOptions = [
    { value: "", label: translations.labels?.all || "All" },
    ...classOptions,
  ];

  // Filtered data based on search
  const filteredData = useMemo(() => {
    if (!apiSearch) return scheduleList;
    const search = apiSearch.toLowerCase();
    return scheduleList.filter(
      (item) =>
        (item.exam_type || "").toLowerCase().includes(search) ||
        (item.domain_id || "").toLowerCase().includes(search) ||
        (item.section_id || "").toLowerCase().includes(search)
    );
  }, [scheduleList, apiSearch]);

  // Paginated data
  const paginatedData = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    return filteredData
      .slice(start, start + pagination.pageSize)
      .map((item, index) => ({
        ...item,
        sno: start + index + 1,
      }));
  }, [filteredData, pagination]);

  // Handlers
  const handleExamChange = (value) => {
    setSelectedExam(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleClassChange = (value) => {
    setSelectedClass(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleAdd = () => {
    navigate("/user/examScheduleCreate");
  };

  const handleView = useCallback(
    (row) => {
      navigate(`/user/examScheduleView`, {
        state: {
          scheduleData: row,
        },
      });
    },
    [navigate]
  );

  // Table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: "sno",
        header: translations.labels?.serialNo || "S.No",
        size: 80,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "exam_type",
        header: translations.labels?.examName || "Exam Name",
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ row }) => row.original.exam_type || "-",
      },
      {
        accessorKey: "domain_id",
        header: translations.labels?.class || "Class",
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ row }) => row.original.domain_id || "-",
      },
      {
        accessorKey: "section_id",
        header: translations.labels?.section || "Section",
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ row }) => row.original.section_id || "-",
      },
      {
        id: "actions",
        header: translations.labels?.actions || "Actions",
        size: 100,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <button
            className={styles.viewBtn}
            onClick={() => handleView(row.original)}
            title={translations.labels?.view || "View"}
          >
            <FaEye />
          </button>
        ),
      },
    ],
    [handleView, translations]
  );

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaCalendarAlt />
        </div>
        <h1 className={styles.headerTitle}>
          {translations.labels?.examSchedule || "Exam Schedule"}
        </h1>
      </div>

      {/* Filters + Actions */}
      <div className={styles.filterSection}>
        <div className="row align-items-end">
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={translations.labels?.academicYear || "Academic Year"}
              name="academicYear"
              type="text"
              value={academic_year}
              disabled
              readOnly
            />
          </div>

          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={translations.labels?.examName || "Exam Name"}
              name="examName"
              value={selectedExam}
              onChange={handleExamChange}
              options={examNameOptions}
              placeholder={translations.labels?.allExams || "All Exams"}
            />
          </div>

          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={translations.labels?.class || "Class"}
              name="class"
              value={selectedClass}
              onChange={handleClassChange}
              options={classDropdownOptions}
              placeholder={translations.labels?.all || "All"}
            />
          </div>

          <div className="col-lg-3 px-2 col-md-12 col-sm-12">
            <div className={styles.actions}>
              <CustomButton
                type="button"
                variant="contained"
                className={styles.addButton}
                fullWidth={false}
                onClick={handleAdd}
              >
                 <FaPlus style={{ marginRight: "8px" }} />
                {translations.labels?.add || "ADD"}
              </CustomButton>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <CustomMRTTable
        columns={columns}
        data={paginatedData}
        loading={loading}
        pagination={pagination}
        enableSearch={false}
        enableFiltering={false}
        rowCount={filteredData.length}
        onPaginationChange={setPagination}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        onSearchEnter={(value) => {
          setApiSearch(value);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }}
      />
    </div>
  );
};

export default ExamScheduleList;